import { useEffect } from 'react';
import './HurricaneAnimation.css';

/**
 * PROFESSIONAL HURRICANE ANIMATION
 * Flying-type special move - whips up a fierce windstorm
 * 
 * Key Features:
 * - Flying-type special attack (110 base power, 70% accuracy)
 * - 30% chance to confuse
 * - Massive tornado/cyclone engulfing the opponent
 * - Professional gaming quality with swirling wind effects
 */
export default function HurricaneAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const elements = [];

    // PHASE 1: Wind Gathering (0-500ms)
    playerEl.classList.add('hurricane-user-charge');

    const windAura = document.createElement('div');
    windAura.className = 'hurricane-wind-aura';
    windAura.style.left = `${playerX - 80}px`;
    windAura.style.top = `${playerY - 80}px`;
    container.appendChild(windAura);
    elements.push(windAura);

    // Wind streaks around player
    for (let i = 0; i < 10; i++) {
      const streak = document.createElement('div');
      streak.className = 'hurricane-wind-streak';
      const angle = (i / 10) * 360;
      const radius = 65;
      streak.style.left = `${playerX + Math.cos(angle * Math.PI / 180) * radius - 3}px`;
      streak.style.top = `${playerY + Math.sin(angle * Math.PI / 180) * radius - 12}px`;
      streak.style.transform = `rotate(${angle + 90}deg)`;
      streak.style.animationDelay = `${i * 40}ms`;
      container.appendChild(streak);
      elements.push(streak);
    }

    // PHASE 2: Tornado Formation at Opponent (500-1200ms)
    const phase2Timer = setTimeout(() => {
      // Main tornado vortex
      const tornado = document.createElement('div');
      tornado.className = 'hurricane-tornado';
      tornado.style.left = `${opponentX - 50}px`;
      tornado.style.top = `${opponentY - 100}px`;
      container.appendChild(tornado);
      elements.push(tornado);

      // Swirling wind rings
      for (let i = 0; i < 6; i++) {
        const ringTimer = setTimeout(() => {
          const ring = document.createElement('div');
          ring.className = 'hurricane-vortex-ring';
          ring.style.left = `${opponentX - 40 - i * 3}px`;
          ring.style.top = `${opponentY - 80 + i * 25}px`;
          ring.style.width = `${80 + i * 6}px`;
          ring.style.height = `${25 + i * 2}px`;
          ring.style.animationDelay = `${i * 60}ms`;
          container.appendChild(ring);
          elements.push(ring);
        }, i * 60);
        elements.push({ timer: ringTimer, type: 'timer' });
      }

      // Debris particles swirling
      for (let i = 0; i < 16; i++) {
        const debrisTimer = setTimeout(() => {
          const debris = document.createElement('div');
          debris.className = 'hurricane-debris';
          const angle = (i / 16) * 360;
          const radius = 40 + Math.random() * 30;
          debris.style.left = `${opponentX + Math.cos(angle * Math.PI / 180) * radius - 4}px`;
          debris.style.top = `${opponentY + Math.sin(angle * Math.PI / 180) * radius * 0.5 - 4}px`;
          debris.style.setProperty('--orbit-radius', `${radius}px`);
          debris.style.animationDelay = `${i * 35}ms`;
          container.appendChild(debris);
          elements.push(debris);
        }, i * 35);
        elements.push({ timer: debrisTimer, type: 'timer' });
      }

      // Wind gust lines
      for (let i = 0; i < 8; i++) {
        const gustTimer = setTimeout(() => {
          const gust = document.createElement('div');
          gust.className = 'hurricane-gust';
          gust.style.left = `${opponentX + (Math.random() - 0.5) * 120 - 20}px`;
          gust.style.top = `${opponentY + (Math.random() - 0.5) * 80 - 3}px`;
          container.appendChild(gust);
          elements.push(gust);
        }, i * 50);
        elements.push({ timer: gustTimer, type: 'timer' });
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Hurricane Impact (1200-1700ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('hurricane-opponent-hit');

      // Wind explosion
      const explosion = document.createElement('div');
      explosion.className = 'hurricane-explosion';
      explosion.style.left = `${opponentX - 100}px`;
      explosion.style.top = `${opponentY - 100}px`;
      container.appendChild(explosion);
      elements.push(explosion);

      // Wind burst particles
      for (let i = 0; i < 18; i++) {
        const burstAngle = (i / 18) * 360;
        const burstDist = 55 + Math.random() * 45;
        const burst = document.createElement('div');
        burst.className = 'hurricane-burst';
        burst.style.left = `${opponentX - 4}px`;
        burst.style.top = `${opponentY - 4}px`;
        burst.style.setProperty('--scatter-x', `${Math.cos(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.setProperty('--scatter-y', `${Math.sin(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.animationDelay = `${i * 15}ms`;
        container.appendChild(burst);
        elements.push(burst);
      }

      // Shockwaves
      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'hurricane-shockwave';
          sw.style.left = `${opponentX - 70}px`;
          sw.style.top = `${opponentY - 70}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }
    }, 1200);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Wind Settle (1700-2000ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('hurricane-user-charge');

      for (let i = 0; i < 6; i++) {
        const settle = document.createElement('div');
        settle.className = 'hurricane-wind-settle';
        settle.style.left = `${opponentX + (Math.random() - 0.5) * 100 - 15}px`;
        settle.style.top = `${opponentY + (Math.random() - 0.5) * 60 - 3}px`;
        settle.style.animationDelay = `${i * 40}ms`;
        container.appendChild(settle);
        elements.push(settle);
      }
    }, 1700);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (2000ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('hurricane-user-charge');
      opponentEl.classList.remove('hurricane-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 2000);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('hurricane-user-charge');
      opponentEl.classList.remove('hurricane-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

HurricaneAnimation.moveType = 'flying';
